import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import { EmbeddedCheckoutProvider, EmbeddedCheckout } from '@stripe/react-stripe-js';
import { SEO } from '../components/SEO';

let stripePromise = null;
let stripeFetched = false;

const getStripe = async () => {
  if (!stripeFetched) {
    stripeFetched = true;
    try {
      const res = await fetch('/billing/stripe-config');
      const { publishableKey } = await res.json();
      if (publishableKey) {
        stripePromise = loadStripe(publishableKey);
      }
    } catch (err) {
      stripeFetched = false; // Allow retry on network error
      console.error('Failed to load Stripe config:', err);
    }
  }
  return stripePromise;
};

export const SetupPayment = () => {
  const navigate = useNavigate();
  const [stripeInstance, setStripeInstance] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const init = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        navigate('/login', { replace: true });
        return;
      }

      // Check if user actually needs payment setup
      try {
        const profileRes = await fetch('/auth/profile', {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (profileRes.ok) {
          const data = await profileRes.json();
          const status = data.subscription?.status;
          if (status && status !== 'pending_payment') {
            // Already has active/trialing subscription, go to dashboard
            navigate('/dashboard', { replace: true });
            return;
          }
        } else if (profileRes.status === 401) {
          localStorage.removeItem('access_token');
          localStorage.removeItem('token');
          navigate('/login', { replace: true });
          return;
        }
      } catch (_) {
        // Continue to show payment page even if profile check fails
      }

      const stripe = await getStripe();
      if (!stripe) {
        setError('Payment system is not configured. Please contact support.');
        setLoading(false);
        return;
      }

      setStripeInstance(stripe);
      setLoading(false);
    };

    init();
  }, [navigate]);

  const fetchClientSecret = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    const baseUrl = window.location.origin;

    const res = await fetch('/billing/create-signup-checkout', {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        successUrl: `${baseUrl}/setup-payment?status=complete&session_id={CHECKOUT_SESSION_ID}`,
        cancelUrl: `${baseUrl}/setup-payment`,
      }),
    });

    const data = await res.json();

    if (!data.success || !data.clientSecret) {
      throw new Error(data.message || 'Failed to create checkout session');
    }

    return data.clientSecret;
  }, []);

  // Handle return from completed checkout
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('status') === 'complete') {
      setLoading(true);

      const verifyPayment = async () => {
        const token = localStorage.getItem('access_token');
        try {
          const res = await fetch('/billing/verify-payment', {
            method: 'POST',
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
          });
          const data = await res.json();
          if (data.success) {
            navigate('/dashboard', { replace: true });
          } else {
            // Payment may still be processing; wait and retry once
            setTimeout(async () => {
              const retryRes = await fetch('/billing/verify-payment', {
                method: 'POST',
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Content-Type': 'application/json',
                },
              });
              const retryData = await retryRes.json();
              if (retryData.success) {
                navigate('/dashboard', { replace: true });
              } else {
                setError('Payment is being processed. Please refresh in a moment.');
                setLoading(false);
              }
            }, 3000);
          }
        } catch (err) {
          setError('Failed to verify payment. Please refresh the page.');
          setLoading(false);
        }
      };

      verifyPayment();
    }
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('token');
    navigate('/login', { replace: true });
  };

  return (
    <>
      <SEO title="Setup Payment — GHLWA" />
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="max-w-3xl mx-auto flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-gray-800">Complete Your Subscription</h1>
              <p className="text-sm text-gray-500 mt-0.5">Set up payment to access your dashboard</p>
            </div>
            <button
              onClick={handleLogout}
              className="text-sm text-gray-500 hover:text-gray-700 underline"
            >
              Log out
            </button>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 py-10">
          {/* Pricing info */}
          <div className="mb-8 bg-white rounded-xl border border-gray-200 p-6">
            <h2 className="font-semibold text-gray-800 mb-3">Volume Pricing</h2>
            <div className="grid grid-cols-3 gap-4 text-center">
              {[
                { range: '1–3', rate: '$27', label: 'Starter' },
                { range: '4–10', rate: '$24', label: 'Growth' },
                { range: '11+', rate: '$18', label: 'Scale' },
              ].map((tier) => (
                <div key={tier.label} className="p-3 rounded-lg bg-gray-50">
                  <p className="text-lg font-bold text-indigo-700">{tier.rate}<span className="text-xs font-normal text-gray-500">/mo</span></p>
                  <p className="text-xs text-gray-500">{tier.range} subaccounts</p>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-400 mt-3">
              You're subscribing for your first subaccount. Price adjusts automatically as you add more.
            </p>
          </div>

          {/* Checkout / Error / Loading */}
          {loading && (
            <div className="flex flex-col items-center justify-center py-16">
              <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mb-4" />
              <p className="text-gray-500">Loading checkout...</p>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
              <p className="text-red-700 font-medium mb-3">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm hover:bg-red-700"
              >
                Try Again
              </button>
            </div>
          )}

          {!loading && !error && stripeInstance && (
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <EmbeddedCheckoutProvider
                stripe={stripeInstance}
                options={{ fetchClientSecret }}
              >
                <EmbeddedCheckout />
              </EmbeddedCheckoutProvider>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default SetupPayment;
